import { formatDec, isDecimalText, utcToLocal } from '@felix/domain';
import { ActionIcon, Alert, Button, FileButton, Modal, Select, Textarea } from '@mantine/core';
import {
  IconAlertCircle,
  IconArrowBackUp,
  IconCheck,
  IconCircleCheck,
  IconDeviceFloppy,
  IconEdit,
  IconFileTypePdf,
  IconPaperclip,
  IconPlus,
  IconRotate2,
  IconSend,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { errorCode } from '../personnel/source.js';
import { fileSize } from '../pms/AttachmentCard.js';
import classes from './Ledger.module.css';
import type {
  FuelLedgerCan,
  FuelRecordDetail,
  FuelRecordStatus,
  FuelRecordSummary,
} from './types.js';

/**
 * Y2 ekranlarının ortak parçaları (ADR 0117 Ek Y2): biçimler, bölüm bandı, dosya alanı, kayıt
 * seçici, durum rozeti, onay akışının düğmeleri ve pencereleri.
 */

/** MT gösterimi: iki ondalık (yalnız gösterim; ADR 0117 §11). */
export function mt(value: string | null | undefined, digits = 2): string {
  if (value === null || value === undefined || !isDecimalText(value)) return '—';
  return formatDec(value, digits);
}

/** Gemi yerel `YYYY-MM-DDTHH:mm` → `GG.AA.YYYY SS:DD`. */
export function localText(local: string | null | undefined): string {
  if (!local) return '—';
  const [day = '', time = ''] = local.split('T');
  const [y, m, d] = day.split('-');
  return `${d}.${m}.${y} ${time.slice(0, 5)}`.trim();
}

/** UTC an + kaydın saat farkı → gemi yerel `GG.AA.YYYY SS:DD`. */
export function atText(utc: string | null | undefined, offset: number): string {
  return utc ? localText(utcToLocal(utc, offset)) : '—';
}

/** İstemcinin idempotency anahtarı (ADR 0117 §5): form açılırken üretilir. */
export function newKey(): string {
  return crypto.randomUUID();
}

/** Hata metni: `fuel.errors.<KOD>`, yoksa genel ileti. */
export function useLedgerError() {
  const { t } = useTranslation();
  return (error: unknown) =>
    t(`fuel.errors.${errorCode(error)}`, { defaultValue: t('fuel.errors.GENERIC') });
}

/** Sunucunun alan ayrıntısı (`alan:KOD,…`) → alan → kod. */
export function issuesOf(error: unknown): Record<string, string> {
  const detail = (error as { detail?: unknown } | null)?.detail;
  if (typeof detail !== 'string') return {};
  const issues: Record<string, string> = {};
  for (const part of detail.split(',')) {
    const [path, code] = part.split(':');
    if (path && code && issues[path] === undefined) issues[path] = code;
  }
  return issues;
}

/** Durum etiketi (token renkleri; koyu metin, açık zemin: okunur karşıtlık). */
export function StatusBadge({ status }: { status: FuelRecordStatus }) {
  const { t } = useTranslation();
  return (
    <span className={`${classes.statusBadge} ${classes[`status_${status}`] ?? ''}`}>
      {t(`fuel.records.status.${status}`)}
    </span>
  );
}

export function Section({
  icon,
  title,
  action,
  children,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className={classes.section} aria-label={title}>
      <div className={classes.sectionHead}>
        <h3 className={classes.sectionTitle}>
          <span className={classes.sectionIcon} aria-hidden>
            {icon}
          </span>
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Kayıt seçici: kayıt numarası ve durumu; "Yeni" yeni taslak formu açar. */
export function RecordPicker({
  records,
  selectedId,
  onSelect,
  onNew,
  canCreate,
}: {
  records: readonly FuelRecordSummary[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  canCreate: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className={classes.picker}>
      <Select
        className={classes.pickerSelect}
        aria-label={t('fuel.records.label')}
        placeholder={records.length === 0 ? t('fuel.records.empty') : t('fuel.records.label')}
        data={records.map((r) => ({
          value: r.id,
          label: `${r.recordNo} · ${t(`fuel.records.status.${r.status}`)}`,
        }))}
        value={selectedId}
        allowDeselect={false}
        disabled={records.length === 0}
        onChange={(value) => {
          if (value) onSelect(value);
        }}
      />
      {canCreate ? (
        <Button variant="default" leftSection={<IconPlus size={16} />} onClick={onNew}>
          {t('fuel.records.new')}
        </Button>
      ) : null}
    </div>
  );
}

/** Kaydın durum bandı: iade gerekçesi, bekleyen onay, onay, ters kayıt, düzeltme. */
export function StatusBanner({
  detail,
  people,
  correctsNo,
}: {
  detail: FuelRecordDetail | null;
  people: ReadonlyMap<string, string>;
  correctsNo: string | null;
}) {
  const { t } = useTranslation();
  if (!detail) return null;
  const banners: ReactNode[] = [];
  const at = (iso: string) => atText(iso, detail.tzOffsetMin);
  if (detail.correctsId && correctsNo && detail.status !== 'approved') {
    banners.push(
      <Alert key="correction" className={classes.banner} color="grape" role="note">
        {t('fuel.records.correctionOf', { no: correctsNo })}
      </Alert>,
    );
  }
  if (detail.status === 'returned' && detail.approval.returned) {
    banners.push(
      <Alert
        key="returned"
        className={classes.banner}
        color="orange"
        icon={<IconArrowBackUp size={18} />}
        role="alert"
      >
        {t('fuel.records.returnedBanner', {
          by: people.get(detail.approval.returned.by) ?? '—',
          at: at(detail.approval.returned.at),
          reason: detail.approval.returned.reason ?? '',
        })}
      </Alert>,
    );
  }
  // Gönderilmiş ve onaylı kayıtta durum rozeti yeter (tasarımda bant yok); onay anı başlıkta
  if (detail.status === 'corrected') {
    banners.push(
      <Alert key="corrected" className={classes.banner} color="grape" role="status">
        {t('fuel.records.correctedBanner')}
      </Alert>,
    );
  }
  if (detail.status === 'reversed') {
    banners.push(
      <Alert key="reversed" className={classes.banner} color="red" role="status">
        {t('fuel.records.reversedBanner', {
          at: at(detail.reversedAt ?? ''),
          reason: detail.reversalReason ?? '',
        })}
      </Alert>,
    );
  }
  return <>{banners}</>;
}

/** Hata ve başarı bildirimi. */
export function Notices({ error, done }: { error: string | null; done: string | null }) {
  return (
    <>
      {error ? (
        <Alert
          className={classes.banner}
          color="red"
          icon={<IconAlertCircle size={18} />}
          role="alert"
        >
          {error}
        </Alert>
      ) : null}
      {done ? (
        <Alert
          className={classes.banner}
          color="green"
          icon={<IconCircleCheck size={18} />}
          role="status"
        >
          {done}
        </Alert>
      ) : null}
    </>
  );
}

/** Dosya alanı (BDN, analiz raporu, fatura): kayıtlı belge ya da seçilmiş dosya; kaldır. */
export function FileField({
  label,
  required,
  file,
  pending,
  editable,
  onPick,
  onRemove,
  error,
}: {
  label: string;
  required?: boolean;
  file: { fileName: string; size: number } | null;
  pending: File | null;
  editable: boolean;
  onPick: (file: File) => void;
  onRemove: () => void;
  error?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const shown = pending ? { fileName: pending.name, size: pending.size } : file;
  return (
    <div>
      <span className={classes.fileLabel}>
        {label}
        {required ? (
          <span aria-hidden style={{ color: 'var(--felix-danger-fg)', marginLeft: 4 }}>
            *
          </span>
        ) : null}
      </span>
      {shown ? (
        <div className={classes.file}>
          <IconFileTypePdf size={26} className={classes.fileIcon} aria-hidden />
          <span className={classes.fileText}>
            <span className={classes.fileName} title={shown.fileName}>
              {shown.fileName}
            </span>
            <span className={classes.fileMeta}>
              {fileSize(shown.size, i18n.resolvedLanguage ?? 'en')}
            </span>
          </span>
          {editable ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onRemove}
              aria-label={t('fuel.file.remove', { name: shown.fileName })}
            >
              <IconX size={18} />
            </ActionIcon>
          ) : (
            <span />
          )}
        </div>
      ) : (
        <div className={classes.fileEmpty}>
          <span>{t('fuel.file.none')}</span>
          {editable ? (
            <FileButton
              accept="application/pdf,image/jpeg,image/png"
              onChange={(picked) => {
                if (picked) onPick(picked);
              }}
            >
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  leftSection={<IconPaperclip size={14} />}
                  aria-label={`${t('fuel.file.choose')}: ${label}`}
                >
                  {t('fuel.file.choose')}
                </Button>
              )}
            </FileButton>
          ) : null}
        </div>
      )}
      {error ? (
        <span role="alert" style={{ color: 'var(--felix-danger-fg)', fontSize: '0.75rem' }}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

export type WorkflowAction = 'save' | 'submit' | 'approve' | 'return' | 'correct' | 'reverse';

/** Kaydın durumuna ve yetkiye göre eylemler (ortak belgesi §7, §8 "Düğmeler"). */
export function workflowActions(
  status: FuelRecordStatus | 'new',
  can: FuelLedgerCan,
  editor: boolean,
): WorkflowAction[] {
  if (!editor) return [];
  if (status === 'new' || status === 'draft' || status === 'returned') {
    const actions: WorkflowAction[] = [];
    if (status === 'new' ? can.create : can.edit || can.create) actions.push('save');
    if (can.submit) actions.push('submit');
    return actions;
  }
  if (status === 'submitted') {
    const actions: WorkflowAction[] = [];
    if (can.return) actions.push('return');
    if (can.approve) actions.push('approve');
    return actions;
  }
  if (status === 'approved') {
    const actions: WorkflowAction[] = [];
    if (can.void) actions.push('reverse');
    if (can.create) actions.push('correct');
    return actions;
  }
  return [];
}

const ICONS: Record<WorkflowAction, ReactNode> = {
  save: <IconDeviceFloppy size={18} />,
  submit: <IconSend size={18} />,
  approve: <IconCheck size={18} />,
  return: <IconArrowBackUp size={18} />,
  correct: <IconEdit size={18} />,
  reverse: <IconRotate2 size={18} />,
};

const LABELS: Record<WorkflowAction, string> = {
  save: 'saveDraft',
  submit: 'submit',
  approve: 'approve',
  return: 'return',
  correct: 'correct',
  reverse: 'reverse',
};

export function WorkflowButtons({
  actions,
  busy,
  onAction,
}: {
  actions: readonly WorkflowAction[];
  busy: WorkflowAction | null;
  onAction: (action: WorkflowAction) => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      {actions.map((action) => {
        const primary = action === 'submit' || action === 'approve';
        return (
          <Button
            key={action}
            size="md"
            variant={primary ? 'filled' : action === 'reverse' ? 'light' : 'default'}
            {...(action === 'reverse' ? { color: 'red' } : {})}
            leftSection={ICONS[action]}
            loading={busy === action}
            disabled={busy !== null && busy !== action}
            onClick={() => onAction(action)}
          >
            {t(`fuel.records.${LABELS[action]}`)}
          </Button>
        );
      })}
    </>
  );
}

/**
 * Karar penceresi: onay (bilgi + parola ayara göre) ya da gerekçeli iade; ters kayıt da gerekçe
 * ister. Parola şirket ayarı açıkken (ADR 0075).
 */
export function DecisionModal({
  mode: requested,
  recordNo,
  onClose,
  onConfirm,
}: {
  mode: 'approve' | 'return' | 'reverse' | null;
  recordNo: string;
  onClose: () => void;
  onConfirm: (input: { reason: string; password?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const reauth = useReauth('fuel_bunkering');
  const errorText = useLedgerError();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Kapanış geçişinde pencere son türünün metniyle kalır (başlık ve düğme değişmez)
  const [last, setLast] = useState(requested);
  useEffect(() => {
    if (requested) setLast(requested);
  }, [requested]);
  const mode = requested ?? last;
  const needsReason = mode === 'return' || mode === 'reverse';
  const title =
    mode === 'approve'
      ? t('fuel.records.approveTitle', { no: recordNo })
      : mode === 'return'
        ? t('fuel.records.returnTitle')
        : t('fuel.records.reverseTitle');
  const close = () => {
    setReason('');
    setError(null);
    reauth.reset();
    onClose();
  };
  return (
    <Modal opened={requested !== null} onClose={close} title={title} centered>
      <p style={{ marginTop: 0 }}>
        {mode === 'approve'
          ? t('fuel.records.approveHint')
          : mode === 'return'
            ? t('fuel.records.returnHint')
            : t('fuel.records.reverseHint')}
      </p>
      {needsReason ? (
        <Textarea
          label={
            mode === 'return' ? t('fuel.records.returnReason') : t('fuel.records.reverseReason')
          }
          withAsterisk
          autosize
          minRows={3}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.currentTarget.value)}
          mb="sm"
        />
      ) : null}
      {mode === 'reverse' ? null : (
        <ReauthField reauth={reauth} label={t('fuel.errors.REAUTHENTICATION_REQUIRED')} />
      )}
      {error ? (
        <Alert color="red" role="alert" mt="sm">
          {error}
        </Alert>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        <Button variant="default" onClick={close} disabled={busy}>
          {t('fuel.records.cancel')}
        </Button>
        <Button
          {...(mode === 'approve' ? {} : { color: mode === 'return' ? 'orange' : 'red' })}
          loading={busy}
          disabled={
            (needsReason && reason.trim().length < 3) || (mode !== 'reverse' && reauth.missing)
          }
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const body = reauth.body();
              await onConfirm({
                reason: reason.trim(),
                ...(body.password ? { password: body.password } : {}),
              });
              close();
            } catch (failure) {
              reauth.noteError(failure);
              setError(errorText(failure));
            } finally {
              setBusy(false);
            }
          }}
        >
          {mode === 'approve'
            ? t('fuel.records.approve')
            : mode === 'return'
              ? t('fuel.records.return')
              : t('fuel.records.reverse')}
        </Button>
      </div>
    </Modal>
  );
}

/** Ofis: gemi verisi salt okunur notu. */
export function OfficeNote() {
  const { t } = useTranslation();
  return (
    <Alert className={classes.banner} color="blue" role="note">
      {t('fuel.records.officeReadOnly')}
    </Alert>
  );
}

/** Boş metin `null`; kırpılır. */
export function orNull(value: string | null | undefined): string | null {
  const text = (value ?? '').trim();
  return text === '' ? null : text;
}

/** `YYYY-MM-DD` ve `SS:DD` → yerel `YYYY-MM-DDTHH:mm`; biri eksikse `null`. */
export function joinLocal(day: string | null, time: string | null): string | null {
  if (!day || !time) return null;
  return `${day}T${time.slice(0, 5)}`;
}

export function splitLocal(local: unknown): { day: string | null; time: string | null } {
  if (typeof local !== 'string' || !local.includes('T')) return { day: null, time: null };
  const [day = '', time = ''] = local.split('T');
  return { day: day || null, time: time.slice(0, 5) || null };
}
